/**
 * iter265/267 — Façade de l'assistant interne (admin).
 * Bouton flottant + panneau de discussion : on pose une question en une phrase
 * (« où en est l'OR 2026-3017 ? », « combien reste-t-il à encaisser ? ») et l'assistant
 * lit le CRM pour répondre : atelier, facturation, caisse, livre comptable,
 * dépenses rapides, pointage et gardiennage des pneus.
 * Lot 2 (iter284) : l'assistant peut aussi préparer des actions (rappel, rendez-vous,
 * statut d'un OR, encaissement…) — rien n'est exécuté avant un clic sur « Confirmer ».
 */
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, X, Send, Loader2, Wrench, Plus, Paperclip } from "lucide-react";
import AssistantActionCard from "@/components/assistant/AssistantActionCard";
import api from "@/lib/api";

const SUGGESTIONS = [
  "Qu'est-ce qui demande mon attention aujourd'hui ?",
  "Combien reste-t-il à encaisser à la caisse aujourd'hui ?",
  "Quelles factures en retard dois-je relancer en premier ?",
  "Combien de dépenses rapides attendent ma validation ?",
  "Qui a des heures manquantes au pointage ce mois-ci ?",
  "Quels gardiennages de pneus sont à facturer ?",
];

export default function AssistantFab() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [texte, setTexte] = useState("");
  const [busy, setBusy] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  // iter284 — résultats des vérifications programmées
  const [rapports, setRapports] = useState([]);
  const fin = useRef(null);
  const fichierRef = useRef(null);

  useEffect(() => { fin.current?.scrollIntoView({ block: "nearest" }); }, [messages, busy]);

  const chargerRapports = () => {
    api.get("/assistant/rapports", { params: { non_lus: true } })
      .then(({ data }) => setRapports(data.items || []))
      .catch(() => {});
  };

  useEffect(() => { chargerRapports(); }, []);
  useEffect(() => { if (open) chargerRapports(); }, [open]);

  const vuRapports = async () => {
    await api.post("/assistant/rapports/lus").catch(() => {});
    setRapports([]);
  };

  const envoyer = async (question) => {
    const q = (question ?? texte).trim();
    if (!q || busy) return;
    setTexte("");
    setMessages((m) => [...m, { role: "user", content: q }]);
    setBusy(true);
    try {
      const { data } = await api.post("/assistant/chat", {
        message: q, conversation_id: conversationId,
      });
      setConversationId(data.conversation_id);
      setMessages((m) => [...m, {
        role: "assistant",
        content: data.reply || "Je n'ai pas de réponse à donner.",
        tools: data.tools_used || [],
        actions: data.pending_actions || [],
      }]);
    } catch (e) {
      setMessages((m) => [...m, {
        role: "assistant",
        content: e.response?.data?.detail || "L'assistant est momentanément indisponible.",
        erreur: true,
      }]);
    } finally {
      setBusy(false);
    }
  };

  const nouvelle = () => { setMessages([]); setConversationId(null); setTexte(""); };

  // iter285 — pièce jointe : CAMT, facture, carte grise ou CSV
  const envoyerFichier = async (file) => {
    if (!file || busy) return;
    const form = new FormData();
    form.append("fichier", file);
    if (texte.trim()) form.append("message", texte.trim());
    if (conversationId) form.append("conversation_id", conversationId);
    setMessages((m) => [...m, { role: "user", content: `📎 ${file.name}${texte.trim() ? " — " + texte.trim() : ""}` }]);
    setTexte("");
    setBusy(true);
    try {
      const { data } = await api.post("/assistant/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setConversationId(data.conversation_id);
      setMessages((m) => [...m, {
        role: "assistant",
        content: data.reply || "Fichier analysé.",
        tools: data.tools_used || [],
        actions: data.pending_actions || [],
      }]);
    } catch (e) {
      setMessages((m) => [...m, {
        role: "assistant",
        content: e.response?.data?.detail || "Fichier non exploitable.",
        erreur: true,
      }]);
    } finally {
      setBusy(false);
      if (fichierRef.current) fichierRef.current.value = "";
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-40 h-14 w-14 rounded-full bg-[#52a675] text-white shadow-lg shadow-[#52a675]/40 flex items-center justify-center hover:bg-[#3d8a5c] hover:scale-105 transition-all"
          title="Assistant atelier"
          data-testid="assistant-fab"
        >
          <Sparkles className="w-6 h-6" />
          {!!rapports.length && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-[10px] font-bold flex items-center justify-center border-2 border-white"
              data-testid="assistant-rapports-badge"
            >
              {rapports.length}
            </span>
          )}
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-4 right-4 z-50 w-[calc(100vw-2rem)] sm:w-[400px] h-[min(600px,calc(100vh-2rem))] bg-white border border-gray-200 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          data-testid="assistant-panel"
        >
          <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-[#f0f9f4]">
            <Sparkles className="w-4 h-4 text-[#52a675]" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-900">Assistant atelier</div>
              <div className="text-[10px] text-gray-500">Atelier, facturation, caisse, compta, pointage, pneus</div>
            </div>
            <button onClick={nouvelle} className="text-gray-400 hover:text-gray-700 p-1" title="Nouvelle conversation" data-testid="assistant-new">
              <Plus className="w-4 h-4" />
            </button>
            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-700 p-1" title="Fermer" data-testid="assistant-close">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {!!rapports.length && (
              <div className="rounded-xl border border-[#52a675]/40 bg-[#f0f9f4] p-2.5 space-y-2" data-testid="assistant-rapports">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] font-semibold text-[#2d5f3f]">
                    {rapports.length} rapport{rapports.length > 1 ? "s" : ""} programmé{rapports.length > 1 ? "s" : ""}
                  </span>
                  <button onClick={vuRapports} className="text-[10px] text-gray-500 hover:text-gray-800" data-testid="assistant-rapports-lus">
                    Marquer comme lus
                  </button>
                </div>
                {rapports.map((r) => (
                  <div key={r.id} className="text-[11px] text-gray-700" data-testid="assistant-rapport-item">
                    <div className="font-medium text-gray-900">{r.libelle}</div>
                    <div className="whitespace-pre-wrap">{r.texte}</div>
                  </div>
                ))}
              </div>
            )}

            {!messages.length && (
              <div className="space-y-2" data-testid="assistant-suggestions">
                <p className="text-xs text-gray-500">Posez votre question, ou commencez par :</p>
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => envoyer(s)}
                    className="w-full text-left text-xs px-3 py-2 rounded-lg border border-gray-200 text-gray-700 hover:border-[#52a675] hover:bg-[#f0f9f4] transition-colors"
                    data-testid="assistant-suggestion"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {messages.map((m, i) => (
              <div
                key={i}
                className={m.role === "user" ? "flex justify-end" : "flex justify-start"}
                data-testid={`assistant-message-${m.role}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#52a675] text-white rounded-br-sm"
                      : m.erreur
                        ? "bg-red-50 text-red-700 border border-red-200 rounded-bl-sm"
                        : "bg-gray-100 text-gray-800 rounded-bl-sm"
                  }`}
                >
                  {m.content}
                  {(m.actions || []).map((a) => (
                    <AssistantActionCard
                      key={a.id}
                      action={a}
                      onDone={(message, ok, suivante) => {
                        setMessages((prev) => prev.map((msg, j) => (
                          j === i
                            ? { ...msg, actions: (msg.actions || []).filter((x) => x.id !== a.id) }
                            : msg
                        )).concat([{
                          role: "assistant",
                          content: (ok ? "✅ " : "⚠️ ") + message,
                          erreur: !ok,
                          actions: suivante ? [suivante] : [],
                        }]));
                      }}
                    />
                  ))}
                  {!!(m.tools || []).length && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {m.tools.map((t) => (
                        <span key={t} className="inline-flex items-center gap-0.5 text-[9px] text-gray-500 bg-white border border-gray-200 rounded-full px-1.5 py-0.5">
                          <Wrench className="w-2.5 h-2.5" /> {t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {busy && (
              <div className="flex items-center gap-2 text-xs text-gray-400" data-testid="assistant-loading">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> L&apos;assistant consulte le CRM…
              </div>
            )}
            <div ref={fin} />
          </div>

          <div className="border-t border-gray-100 p-2 flex items-end gap-2">
            <input
              ref={fichierRef}
              type="file"
              accept=".xml,.pdf,.csv,image/*"
              className="hidden"
              onChange={(e) => envoyerFichier(e.target.files?.[0])}
              data-testid="assistant-file-input"
            />
            <Button
              variant="ghost"
              onClick={() => fichierRef.current?.click()}
              disabled={busy}
              className="h-10 w-9 p-0 flex-shrink-0 text-gray-500 hover:text-[#52a675]"
              title="Joindre un relevé CAMT, une facture, une carte grise ou un CSV"
              data-testid="assistant-attach"
            >
              <Paperclip className="w-4 h-4" />
            </Button>
            <Textarea
              value={texte}
              onChange={(e) => setTexte(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); envoyer(); }
              }}
              placeholder="Votre question en une phrase…"
              rows={1}
              className="resize-none text-sm min-h-[40px] max-h-28"
              data-testid="assistant-input"
            />
            <Button
              onClick={() => envoyer()}
              disabled={busy || !texte.trim()}
              className="h-10 w-10 p-0 bg-[#52a675] hover:bg-[#3d8a5c] flex-shrink-0"
              data-testid="assistant-send"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
